import { canSeeBuyFromSalesPartnerButton } from "~/feature/SalesPartnerStockIntegration/salesPartnerStockIntegrationHelper";
import { useBuyFromSalesPartnerButtonProps } from "~/feature/SalesPartnerStockIntegration/components/BuyFromSalesPartnerButton";
import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";
import { Button, type ButtonProps } from "~/common/components/Button/Button";
import { canSeeAddToCartButton, canSeeWishlistButton } from "~/utils/features";
import { Icon } from "~/common/components/icon/Icon";
import { isProductPMA } from "~/feature/ProductDescriptor/v1/productDescriptorUtils";
import { IconType } from "~/common/components/icon/IconType";
import { Trans } from "react-i18next";
import { userIsCustomer, userIsSalesman } from "~/utils/roles";

import { useEwellixRequestAQuoteButtonProps } from "~/common/components/Button/PreconfiguredButtons/EwellixRequestAQuoteButton";
import { IconButton } from "@mui/material";
import type { ReactNode } from "react";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import { BookmarkBorderOutlined } from "@mui/icons-material";

type ThemeProps = Pick<ButtonProps, "color" | "variant">;
type ExternalButtonProps = Pick<ButtonProps, "onClick" | "disabled"> & {
  forceChangeToQuote?: boolean;
  quantity?: number;
};

const EWELLIX_BUTTONS: ButtonDefinition[] = [
  {
    key: "ewellixQuote",
    visible: (product: ProductDescriptor) => {
      return true;
    },
    propsKey: "requestAQuote",
    useProps: (product: ProductDescriptor, externalButtonProps: ExternalButtonProps) => {
      const { dialog, ...rest } = useEwellixRequestAQuoteButtonProps(product.base);
      return {
        children: dialog,
        ...rest,
        ...externalButtonProps,
      };
    },
  },
];
const SCHAEFFLER_BUTTONS: ButtonDefinition[] = [
  {
    key: "atcFromSP",
    visible: (product: ProductDescriptor) => canSeeBuyFromSalesPartnerButton(),
    propsKey: "atcFromSP",
    useProps: (product, { onClick, quantity, ...rest }) => {
      return {
        ...useBuyFromSalesPartnerButtonProps({
          onClickExternal: onClick,
          product: product.base,
          quantity: quantity,
          pageType: "SRP",
        }),
        ...rest,
      };
    },
  },
  {
    key: "atc",
    visible: (product: ProductDescriptor) =>
      canSeeAddToCartButton(true) && (userIsCustomer() || !productIsLinear(product)),
    propsKey: "atc",
    useProps: useATCButtonProps,
  },
  {
    key: "wishlist",
    visible: (product: ProductDescriptor) => canSeeWishlistButton(),
    propsKey: "wishlist",
    useProps: useWishlistButtonProps,
  },
];

type ButtonDefinition = {
  key: string;
  visible: (product: ProductDescriptor) => boolean | undefined;
  propsKey: string;
  useProps?: (product: ProductDescriptor, externalButtonProps: ExternalButtonProps) => ExternalButtonProps;
};

type PropsForButtons = {
  atc?: ExternalButtonProps;
  wishlist?: ExternalButtonProps;
  atcFromSP?: ExternalButtonProps & { quantity?: number };
  configurator?: ExternalButtonProps;
};

function getButtonVariant(sideButton: boolean | undefined, index: number) {
  if (index === 0) {
    return "default";
  } else {
    return sideButton ? "side" : "default";
  }
}

function getButtonColor(index: number) {
  if (index === 0) {
    return "primary";
  } else if (index === 1) {
    return "secondary";
  } else {
    return "tertiary";
  }
}

export function renderButtonsNew(
  product: ProductDescriptor,
  externalProps?: PropsForButtons,
  config?: { sideButton?: boolean; maxButtons?: number },
) {
  let buttons = product.base.externalBrand ? EWELLIX_BUTTONS : SCHAEFFLER_BUTTONS;
  buttons = buttons.filter(({ visible }) => visible(product)).slice(0, config?.maxButtons);

  const components = buttons.map(({ key, useProps, propsKey }, index) => {
    const variant = getButtonVariant(config?.sideButton, index);
    const color = getButtonColor(index);
    const props: ExternalButtonProps = externalProps?.[(propsKey as keyof PropsForButtons) ?? ""] ?? {};

    // @ts-expect-error - TODO - Think through the entire renderButtonsNew approach...
    return <ButtonRenderer key={key} variant={variant} color={color} propsHook={() => useProps?.(product, props)} />;
  });

  if (config?.sideButton) {
    return {
      buttons: components.slice(0, 1),
      sideButtons: components.slice(1),
    };
  } else {
    return {
      buttons: components,
      sideButtons: [],
    };
  }
}

function useATCButtonProps(
  product: ProductDescriptor,
  { forceChangeToQuote, ...externalButtonProps }: ExternalButtonProps,
) {
  let cartButtonLabel;
  let icon;
  let testId;
  if (userIsSalesman()) {
    testId = "pdp-add-to-quote";
    cartButtonLabel = "basket.addToQuote";
    icon = <Icon type={IconType.FileList} size={24} />;
  } else if ((userIsCustomer() && isProductPMA(product)) || forceChangeToQuote) {
    testId = "pdp-add-to-cart";
    cartButtonLabel = "cartPage.btn.quote";
    icon = <Icon type={IconType.FileList} size={24} />;
  } else {
    testId = "pdp-add-to-cart";
    cartButtonLabel = "addToCartPopup.add.submitBtn";
    icon = <ShoppingCartOutlinedIcon />;
  }

  return {
    label: <Trans i18nKey={cartButtonLabel} />,
    icon,
    testId,
    ...externalButtonProps,
  };
}

function useWishlistButtonProps(product: ProductDescriptor, externalButtonProps: ExternalButtonProps) {
  return {
    label: <Trans i18nKey={"basket.addToWishlist"} />,
    icon: <BookmarkBorderOutlined />,
    testId: "btn-add-to-wishlist",
    ...externalButtonProps,
  };
}

function productIsLinear(product: ProductDescriptor) {
  return !!product.base.configuratorUrl;
}

interface ButtonRenderProps {
  variant: "default" | "side";
  color: "primary" | "secondary" | "tertiary";
  propsHook?: () => { label: ReactNode; icon: ReactNode; testId: string; children: ReactNode };
}

const ButtonRenderer = ({ variant, color, propsHook }: ButtonRenderProps) => {
  const { label, icon, testId, children, ...rest } = propsHook?.() ?? {};

  let btn;
  switch (variant) {
    case "default": {
      const colorProps = DEFAULT_BUTTON_THEMES[color];
      btn = (
        <Button {...colorProps} data-test-id={testId} startIcon={icon} fullWidth={true} children={label} {...rest} />
      );
      break;
    }

    case "side": {
      const colorProps = SIDE_BUTTON_THEMES[color];
      btn = <IconButton {...colorProps} data-test-id={testId} children={icon} {...rest} />;
      break;
    }

    default:
      throw new Error("Variant not supported");
  }

  return (
    <>
      {btn}
      {children}
    </>
  );
};

type Color = "primary" | "secondary" | "tertiary";
const SIDE_BUTTON_THEMES: Record<Color, { variant: "contained"; color: "primary" | "secondary" }> = {
  primary: { variant: "contained", color: "primary" },
  secondary: { variant: "contained", color: "secondary" },
  tertiary: { variant: "contained", color: "secondary" },
};
const DEFAULT_BUTTON_THEMES: Record<Color, ThemeProps> = {
  primary: { color: "primary" },
  secondary: { color: "secondary" },
  tertiary: { variant: "text" },
};
